import { Link } from "react-router-dom";
import { ArrowUpRight, Instagram, Linkedin, Facebook, Twitter, Phone, Mail, MessageCircle, MapPin } from "lucide-react";
import Magnetic from "@/components/motion/Magnetic";
import { nav, brand, capabilities } from "@/data/content";

const QuickAction = ({ href, icon: Icon, label, value, testid, external }) => (
  <a
    href={href}
    target={external ? "_blank" : undefined}
    rel={external ? "noopener noreferrer" : undefined}
    data-testid={testid}
    className="group flex items-center gap-4 border border-bone/10 bg-bone/[0.03] p-5 transition-colors duration-300 hover:border-accent hover:bg-accent/10"
  >
    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-bone/20 text-accent transition-colors duration-300 group-hover:border-accent group-hover:bg-accent group-hover:text-bone">
      <Icon size={18} />
    </span>
    <div className="min-w-0">
      <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-bone/50">{label}</p>
      <p className="mt-1 truncate text-sm font-semibold text-bone md:text-base">{value}</p>
    </div>
    <ArrowUpRight size={16} className="ml-auto text-bone/40 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-accent" />
  </a>
);

export default function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer data-testid="footer" className="relative overflow-hidden border-t border-line bg-ink text-bone">
      {/* Ambient wordmark */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute -right-24 -bottom-40 select-none font-display text-[46vw] font-black leading-none text-bone/[0.04] md:-right-16 md:-bottom-52 md:text-[26vw]"
      >
        #
      </span>
      {/* Subtle grid ornament */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            "linear-gradient(to right, rgba(244,244,240,0.7) 1px, transparent 1px), linear-gradient(to bottom, rgba(244,244,240,0.7) 1px, transparent 1px)",
          backgroundSize: "64px 64px",
        }}
      />

      <div className="relative mx-auto max-w-[1600px] px-5 pt-16 sm:pt-20 md:pt-24 lg:pt-28 sm:px-8 md:px-10 lg:px-12 xl:px-16">
        {/* Editorial CTA row */}
        <div className="grid gap-8 border-b border-bone/10 pb-12 sm:gap-10 sm:pb-16 lg:grid-cols-12 lg:gap-8 lg:pb-20">
          <div className="lg:col-span-8">
            <p className="mb-6 flex items-center gap-3 text-xs font-bold uppercase tracking-[0.22em] text-accent">
              <span className="h-px w-10 bg-accent" /> Ready when you are
            </p>
            <p className="font-display text-5xl font-black leading-[0.95] tracking-tight md:text-7xl lg:text-8xl">
              Let&rsquo;s build a brand
              <br />
              <span className="font-serif font-normal italic text-accent">worth choosing.</span>
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4 md:mt-10">
              <Magnetic strength={0.25}>
                <Link
                  to="/contact"
                  data-testid="footer-cta"
                  className="group inline-flex items-center gap-3 bg-accent px-7 py-4 text-sm font-bold uppercase tracking-[0.14em] text-ink transition-transform duration-300 hover:-translate-y-0.5"
                >
                  Send Us Your Brief
                  <ArrowUpRight size={18} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
              </Magnetic>
              <a
                href={`https://wa.me/${brand.whatsapp}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-whatsapp"
                className="inline-flex items-center gap-3 border border-bone/30 px-7 py-4 text-sm font-bold uppercase tracking-[0.14em] text-bone transition-colors duration-300 hover:border-accent hover:text-accent"
              >
                <MessageCircle size={16} /> WhatsApp us
              </a>
            </div>
          </div>

          <div className="grid gap-3 lg:col-span-4">
            <QuickAction
              href={brand.phoneHref}
              icon={Phone}
              label="Call"
              value={brand.phone}
              testid="footer-quick-phone"
            />
            <QuickAction
              href={`mailto:${brand.email}`}
              icon={Mail}
              label="Email"
              value={brand.email}
              testid="footer-quick-email"
            />
            <QuickAction
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(brand.location)}`}
              icon={MapPin}
              label="Studio"
              value="Gurgaon · Delhi NCR"
              testid="footer-quick-studio"
              external
            />
          </div>
        </div>

        {/* Navigation columns */}
        <div className="grid gap-10 py-12 sm:gap-12 sm:py-16 lg:grid-cols-12 lg:gap-8 lg:py-20">
          <div className="lg:col-span-4">
            <img src="/hashtag-logo-light.png" alt="HashTag Marketing" className="h-10 w-auto md:h-12" />
            <p className="mt-6 max-w-sm text-sm leading-relaxed text-bone/60">
              An integrated brand &amp; communications company — {brand.yearsClaim} years, {brand.city}.
              Strategy, creative, digital, PR and live experiences under one roof.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href={brand.social.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" data-testid="social-instagram" className="flex h-10 w-10 items-center justify-center rounded-full border border-bone/20 text-bone/70 transition-all duration-300 hover:-translate-y-0.5 hover:border-accent hover:text-accent">
                <Instagram size={16} />
              </a>
              <a href={brand.social.linkedin} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" data-testid="social-linkedin" className="flex h-10 w-10 items-center justify-center rounded-full border border-bone/20 text-bone/70 transition-all duration-300 hover:-translate-y-0.5 hover:border-accent hover:text-accent">
                <Linkedin size={16} />
              </a>
              <a href={brand.social.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" data-testid="social-facebook" className="flex h-10 w-10 items-center justify-center rounded-full border border-bone/20 text-bone/70 transition-all duration-300 hover:-translate-y-0.5 hover:border-accent hover:text-accent">
                <Facebook size={16} />
              </a>
              <a href={brand.social.twitter} target="_blank" rel="noopener noreferrer" aria-label="Twitter" data-testid="social-twitter" className="flex h-10 w-10 items-center justify-center rounded-full border border-bone/20 text-bone/70 transition-all duration-300 hover:-translate-y-0.5 hover:border-accent hover:text-accent">
                <Twitter size={16} />
              </a>
            </div>
          </div>

          <div className="lg:col-span-2">
            <h4 className="text-[10px] font-bold uppercase tracking-[0.22em] text-bone/40">Explore</h4>
            <ul className="mt-5 space-y-3">
              {nav.map((item) => (
                <li key={item.to}>
                  <Link to={item.to} className="link-underline text-base font-medium text-bone/85 hover:text-bone">
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-3">
            <h4 className="text-[10px] font-bold uppercase tracking-[0.22em] text-bone/40">Capabilities</h4>
            <ul className="mt-5 space-y-3">
              {capabilities.map((c) => (
                <li key={c.n}>
                  <Link to="/services" className="link-underline text-base font-medium text-bone/85 hover:text-bone">
                    {c.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-3">
            <h4 className="text-[10px] font-bold uppercase tracking-[0.22em] text-bone/40">Studio</h4>
            <address className="mt-5 not-italic text-sm leading-relaxed text-bone/70">
              {brand.location}
            </address>
            <p className="mt-4 text-sm leading-relaxed text-bone/50" data-testid="footer-seo-line">
              Integrated marketing agency in Gurgaon, Delhi NCR. Brand strategy, creative, digital, PR, events and exhibitions since 2013.
            </p>
            <p className="mt-6 text-[10px] font-bold uppercase tracking-[0.22em] text-bone/40">Hours</p>
            <p className="mt-2 text-sm text-bone/70">Mon &ndash; Fri &nbsp;·&nbsp; 10:00 &ndash; 19:00 IST</p>
          </div>
        </div>

        {/* Bottom strip */}
        <div className="relative flex flex-col gap-6 border-t border-bone/10 py-8 text-xs text-bone/50 md:flex-row md:items-center md:justify-between">
          <p>© {year} {brand.legal}. All rights reserved.</p>
          <p className="hidden md:block">
            Crafted with <span className="text-accent">#</span> in Delhi NCR
          </p>
          <div className="flex flex-wrap gap-6">
            <Link to="/privacy" className="link-underline">Privacy</Link>
            <Link to="/cookies" className="link-underline">Cookies</Link>
            <Link to="/terms" className="link-underline">Terms</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
